import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Row,
  Col,
  Divider,
  Spin,
} from 'antd';

import axios from 'axios';

import { getCompanyImageURL } from '../../settings/state/settings.actions';
import { getExternalFormTemplateById, submitForm } from '../state/forms.actions';
import FormResponder from '../FormResponder';
import config from '../../config';
import { getExternalFormTextList } from '../formHelpers';

const isValidLink = (url = '') => url?.match(/^(?:.{0,5}:)?\/\//) ?? false;

export default function ExternalForm({
  externalFormTemplate,
}) {
  const dispatch = useDispatch();
  const companyImageURL = useSelector((state) => state.settings.company.companyImageURL);

  const [loading, setLoading] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { isCanada, canadaApiUrl } = externalFormTemplate ?? {};

  useEffect(() => {
    if (isCanada && canadaApiUrl) {
      axios.defaults.baseURL = canadaApiUrl;
    }
    return () => {
      axios.defaults.baseURL = config.baseURL;
    };
  }, [isCanada, canadaApiUrl]);

  useEffect(() => {
    if (externalFormTemplate) {
      dispatch(getCompanyImageURL(externalFormTemplate.companyId, true));
      dispatch(getExternalFormTemplateById(externalFormTemplate.id));
      setLoading(false);
    }
  }, [externalFormTemplate]);

  const Header = useMemo(() => {
    const {
      logoURL,
      texts = [],
      name,
    } = externalFormTemplate ?? {};
    const logoSource = logoURL || companyImageURL;

    const textList = getExternalFormTextList(texts);

    return (
      <>
        <Row justify="space-between">
          <Col className="external-form-title-col">
            <span className="external-form-title">
              { name }
            </span>
          </Col>
          <Col className="external-form-branding">
            <img
              src={logoSource}
              alt="Company Logo"
              style={{
                maxHeight: 50,
                maxWidth: 400,
              }}
            />
            {!!textList.length && (
              <div className="external-form-branding-text">
                {textList.map(({ key, value }) => (
                  <div key={key} className="external-form-branding-text-line">
                    {value === '' ? '\u00A0' : value}
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
        <Divider />
      </>
    );
  }, [externalFormTemplate, companyImageURL]);

  const onSubmit = useCallback(async (payload) => {
    setLoading(true);
    const realPayload = {
      ...payload,
      companyId: externalFormTemplate.companyId,
    };
    const res = await dispatch(submitForm(realPayload, true));
    setLoading(false);

    if (res) {
      const { redirectLink } = externalFormTemplate ?? {};
      if (isValidLink(redirectLink)) {
        window.location.replace(redirectLink);
      } else {
        setHasSubmitted(true);
      }
    }
  }, [externalFormTemplate]);

  return (
    <div
      className="login-container"
      style={{ background: externalFormTemplate?.primaryColor, overflowY: 'scroll' }}
    >
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      { !loading && !hasSubmitted && (
        <FormResponder
          visible
          hideCancelButton
          showAsContainer
          draftDisabled
          isExternalForm
          previewDisabled
          header={Header}
          submitButtonStyle={{
            backgroundColor: externalFormTemplate?.accentColor,
            borderColor: externalFormTemplate?.accentColor,
          }}
          cancelButtonStyle={{
            backgroundColor: externalFormTemplate?.secondaryAccentColor,
            borderColor: externalFormTemplate?.secondaryAccentColor,
          }}
          onSubmit={onSubmit}
        />
      )}
      {
        loading && (
          <Row justify="center" align="middle" style={{ height: '100%' }}>
            <Col>
              <Spin size="large" />
            </Col>
          </Row>
        )
      }
      {
        hasSubmitted && (
          <Row justify="center" align="middle" style={{ height: '100%' }}>
            <Col>
              <h1>Thank you for submitting your form!</h1>
            </Col>
          </Row>
        )
      }
    </div>
  );
}

ExternalForm.propTypes = {
  externalFormTemplate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    companyId: PropTypes.string.isRequired,
    primaryColor: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    accentColor: PropTypes.string.isRequired,
    secondaryAccentColor: PropTypes.string.isRequired,
    redirectLink: PropTypes.string,
    logoURL: PropTypes.string,
    texts: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
